import { Button } from '@mui/material';
import {
  CRICKET_BOWLING_COACH_PROFILE_ROLE,
  ProfileRoleTypes,
} from '@strength/db-schema';
import { ProfileRoleType } from '@strength/domain';
import { MdClose } from '@teamworksdev/icons';
import { CellInputWithDropdown } from '../../../../components/sheet/slots/common';
import {
  Cell,
  CellEditBehavior,
  Region,
  Slot,
  SlotRenderContentProps,
} from '../../../../components/sheet/types';
import { relativeCell } from '../../../../components/sheet/utils';
import {
  CoachProfile,
  PROFILE_ROLE_LABELS,
  ProfileRoleAssignment,
} from './types';

const COLUMN_KEYS = {
  COACH: 0,
  ROLE: 1,
} as const;

const HEADER_NAMES = {
  [COLUMN_KEYS.COACH]: 'Coach',
  [COLUMN_KEYS.ROLE]: 'Role',
} as const;

export class ProfileRoleSlot implements Slot<ProfileRoleAssignment> {
  public readonly region: Region;
  public readonly disabled: boolean;
  public readonly gridSubRowSpan: number;
  public readonly bowlingBlockTypeFlag: boolean;

  constructor(
    private readonly anchorCell: Cell,
    private readonly profileRoles: ProfileRoleAssignment[],
    private readonly coaches: CoachProfile[],
    private readonly onUpdateRole: (
      profileRoleId: string,
      role: ProfileRoleType,
    ) => void,
    private readonly onDeleteRole: (profileRoleId: string) => void,
    private readonly onChangeCoach: (
      profileRoleId: string,
      newProfileId: string,
      role: ProfileRoleType,
    ) => void,
    disabled: boolean,
    gridSubRowSpan: number,
    bowlingBlockTypeFlag: boolean,
  ) {
    this.region = {
      anchor: this.anchorCell,
      height: profileRoles.length + 1, // +1 for header
      width: 2,
      isMerged: false,
    };
    this.disabled = disabled;
    this.gridSubRowSpan = gridSubRowSpan;
    this.bowlingBlockTypeFlag = bowlingBlockTypeFlag;
  }

  private getCoachOptions(): { id: string; value: string }[] {
    return this.coaches.map((coach) => ({
      id: coach.id,
      value: this.formatCoachName(coach),
    }));
  }

  private formatCoachName(coach: CoachProfile): string {
    return `${coach.givenName} ${coach.familyName} (${coach.email})`;
  }

  private getRoleOptions(): { id: string; value: string }[] {
    const options = ProfileRoleTypes.map((role) => ({
      id: role,
      value: PROFILE_ROLE_LABELS[role],
    }));

    return this.bowlingBlockTypeFlag
      ? options
      : options.filter(
          (option) => option.id !== CRICKET_BOWLING_COACH_PROFILE_ROLE,
        );
  }

  private isCoachAlreadyAssigned(
    profileId: string,
    currentProfileRoleId: string,
  ): boolean {
    return this.profileRoles.some(
      (pr) => pr.profileId === profileId && pr.id !== currentProfileRoleId,
    );
  }

  public renderContent({ cell, editing, onBlur }: SlotRenderContentProps) {
    const relative = relativeCell(cell, this.region);
    const style = {
      gridColumnStart: relative.x * this.gridSubRowSpan + 1 + this.anchorCell.x,
      gridRowStart: cell.y + 1,
      gridColumnEnd: `span ${this.gridSubRowSpan}`,
      paddingLeft: '8px',
    };

    // Header row
    if (relative.y === 0) {
      return {
        content: HEADER_NAMES[relative.x as keyof typeof HEADER_NAMES],
        style,
        className:
          'bg-gray-100 text-slate-600 text-[11px] font-medium flex items-center',
        overrideGridStyles: true,
      };
    }

    const rowData = this.profileRoles[relative.y - 1];
    if (!rowData) {
      return { content: '', style, overrideGridStyles: true };
    }

    // Coach column
    if (relative.x === COLUMN_KEYS.COACH) {
      const coachOptions = this.getCoachOptions();
      const selectedOption = coachOptions.find(
        (opt) => opt.id === rowData.profileId,
      );

      return {
        content: (
          <ProfileRoleCellContent
            editing={editing}
            editingValue={rowData.profileId}
            displayValue={selectedOption?.value ?? ''}
            options={coachOptions}
            onBlur={onBlur}
            onChange={(value) => {
              this.onChangeCoach(rowData.id, value, rowData.role);
            }}
            getOptionDisabled={(option) =>
              this.isCoachAlreadyAssigned(option.id, rowData.id)
            }
          />
        ),
        style,
        overrideGridStyles: true,
      };
    }

    // Role column with delete button
    if (relative.x === COLUMN_KEYS.ROLE) {
      const roleOptions = this.getRoleOptions();
      const roleOptionsWithCurrent =
        !this.bowlingBlockTypeFlag &&
        rowData.role === CRICKET_BOWLING_COACH_PROFILE_ROLE
          ? [
              { id: rowData.role, value: PROFILE_ROLE_LABELS[rowData.role] },
              ...roleOptions,
            ]
          : roleOptions;
      const selectedOption = roleOptionsWithCurrent.find(
        (opt) => opt.id === rowData.role,
      );

      return {
        content: (
          <div className="flex items-center gap-1 w-full">
            <div className="flex-1">
              <ProfileRoleCellContent
                editing={editing}
                editingValue={rowData.role}
                displayValue={selectedOption?.value ?? ''}
                options={roleOptions}
                onBlur={onBlur}
                onChange={(value) => {
                  this.onUpdateRole(rowData.id, value as ProfileRoleType);
                }}
                getOptionDisabled={() => false}
              />
            </div>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                this.onDeleteRole(rowData.id);
              }}
              sx={{ minWidth: 'auto', padding: '4px' }}
            >
              <MdClose size={16} />
            </Button>
          </div>
        ),
        style,
        overrideGridStyles: true,
      };
    }

    return { content: '', style, overrideGridStyles: true };
  }

  public getEditBehavior(cell: Cell): CellEditBehavior {
    const relative = relativeCell(cell, this.region);
    // Skip editing for header row
    if (relative.y === 0) {
      return { type: 'skip' };
    }
    return { type: 'input', onStart: () => {}, onCommit: () => {} };
  }

  public getRawData(cells: readonly Cell[]) {
    return cells
      .map((cell) => {
        const relative = relativeCell(cell, this.region);
        if (relative.y === 0) return undefined;
        return this.profileRoles[relative.y - 1];
      })
      .filter((data): data is ProfileRoleAssignment => data !== undefined);
  }
}

function ProfileRoleCellContent({
  editing,
  editingValue,
  displayValue,
  options,
  onChange,
  onBlur,
  getOptionDisabled,
}: {
  editing: boolean;
  editingValue: string;
  displayValue: string;
  options: { id: string; value: string }[];
  onChange: (value: string) => void;
  onBlur: () => void;
  getOptionDisabled: (option: { id: string; value: string }) => boolean;
}) {
  if (editing) {
    return (
      <CellInputWithDropdown
        getOptionDisabled={getOptionDisabled}
        options={options}
        value={editingValue}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }

  return (
    <div className="flex text-xs flex-row justify-between items-center w-full h-full">
      <span className="truncate">{displayValue}</span>
    </div>
  );
}
